'use client';

import { getSortedToolCategories } from '@/content/categories';
import { Menu, X } from 'lucide-react';
import Link from 'next/link';
import { Dialog } from 'radix-ui';
import { useEffect, useState } from 'react';

const navLinks = [
  { href: '/tools', label: 'Tools' },
  { href: '/blog', label: 'Blog' },
  { href: '/about', label: 'About' },
];

const triggerButtonClassName =
  'inline-flex size-9 items-center justify-center rounded-lg border border-border text-foreground sm:hidden';

export function MobileNav() {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const categories = getSortedToolCategories();

  useEffect(() => {
    setMounted(true);
  }, []);

  // Radix's Dialog/Portal primitives are known to produce hydration mismatches
  // (aria/id attributes, portal DOM structure) when server-rendered. The drawer
  // is hidden by default anyway, so there's no SSR benefit — render a static,
  // non-interactive trigger button until mounted, then swap in the real dialog.
  if (!mounted) {
    return (
      <button type="button" aria-label="Open menu" className={triggerButtonClassName}>
        <Menu className="size-5" aria-hidden="true" />
      </button>
    );
  }

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <button type="button" aria-label="Open menu" className={triggerButtonClassName}>
          <Menu className="size-5" aria-hidden="true" />
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 data-[state=open]:animate-in data-[state=open]:fade-in" />
        <Dialog.Content className="fixed inset-y-0 right-0 z-50 flex w-[85vw] max-w-sm flex-col overflow-y-auto bg-background p-6 shadow-xl">
          <div className="flex items-center justify-between">
            <Dialog.Title className="font-heading text-lg font-bold text-foreground">
              Menu
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Close menu"
                className="inline-flex size-9 items-center justify-center rounded-lg border border-border text-foreground"
              >
                <X className="size-5" aria-hidden="true" />
              </button>
            </Dialog.Close>
          </div>

          <nav className="mt-8 flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-2.5 text-base font-medium text-foreground hover:bg-muted"
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="mt-6 border-t border-border pt-6">
            <p className="px-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Categories
            </p>
            <div className="mt-2 flex flex-col gap-1">
              {categories.map((category) => (
                <Link
                  key={category.id}
                  href={`/tools?category=${category.id}`}
                  onClick={() => setOpen(false)}
                  className="rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  {category.label}
                </Link>
              ))}
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
